import Phaser from 'phaser';
import type { Side } from '~shared/types';
import { drawMedalBadge } from './HexBoard';

const SLOT_COUNT = 6;
const ICON_R     = 12;   // 150% of the original 8px
const SPACING    = 33;   // 150% of the original 22px
const PAD        = 9;    // 150% of the original 6px

export const MEDAL_TRACK_WIDTH  = PAD * 2 + (SLOT_COUNT - 1) * SPACING + ICON_R * 2;
export const MEDAL_TRACK_HEIGHT = PAD * 2 + ICON_R * 2;

/** Iron Cross medal: steel disc with a black cross-pattée and silver outline. */
export function drawIronCross(g: Phaser.GameObjects.Graphics, cx: number, cy: number, R: number): void {
  g.fillStyle(0x3a3a3a, 1);
  g.fillCircle(cx, cy, R);
  g.lineStyle(1.2, 0x9a9a9a, 0.8);
  g.strokeCircle(cx, cy, R - 0.8);

  // One arm of the cross, pointing "up"; rotated 4x to build the full cross.
  const L = R * 0.78, w = R * 0.42, iw = R * 0.20;
  const base = [
    { x: -w,  y: -L },
    { x:  w,  y: -L },
    { x:  iw, y: -iw },
    { x: -iw, y: -iw },
  ];
  for (let i = 0; i < 4; i++) {
    const a = (i * Math.PI) / 2;
    const cos = Math.cos(a), sin = Math.sin(a);
    const pts = base.map(p => ({
      x: cx + p.x * cos - p.y * sin,
      y: cy + p.x * sin + p.y * cos,
    }));
    g.fillStyle(0x1a1a1a, 1);
    g.fillPoints(pts, true);
    g.lineStyle(1, 0xd8d8d8, 0.85);
    g.strokePoints(pts, true);
  }

  // Sheen
  g.fillStyle(0xffffff, 0.15);
  g.fillCircle(cx - R * 0.3, cy - R * 0.3, R * 0.25);
}

/**
 * Draws a row of `SLOT_COUNT` (6) victory-medal placeholders starting at the
 * top-left corner (x, y). The first `earned` slots show a filled medal.
 * Slots in [earned, toWin) are empty outline circles (claimable).
 * Slots in [toWin, SLOT_COUNT) are rendered dim with a diagonal slash to
 * signal they are not needed in this scenario.
 * `earned` and `toWin` are clamped to [0, SLOT_COUNT].
 */
export function drawMedalTrack(
  g: Phaser.GameObjects.Graphics, x: number, y: number,
  earned: number, side: Side, toWin: number = SLOT_COUNT,
): void {
  g.clear();

  g.fillStyle(0x0d1a0d, 0.55);
  g.fillRoundedRect(x, y, MEDAL_TRACK_WIDTH, MEDAL_TRACK_HEIGHT, 8);

  const activeSlots = Math.max(0, Math.min(SLOT_COUNT, toWin));
  const cy      = y + PAD + ICON_R;
  const clamped = Math.max(0, Math.min(activeSlots, earned));
  for (let i = 0; i < SLOT_COUNT; i++) {
    const cx = x + PAD + ICON_R + i * SPACING;
    if (i >= activeSlots) {
      // Disabled slot: dim circle + diagonal slash
      g.lineStyle(1, 0x555555, 0.20);
      g.strokeCircle(cx, cy, ICON_R);
      const r = ICON_R * 0.52;
      g.lineBetween(cx - r, cy + r, cx + r, cy - r);
    } else if (i < clamped) {
      if (side === 'allies') drawMedalBadge(g, cx, cy);
      else                    drawIronCross(g, cx, cy, ICON_R);
    } else {
      g.lineStyle(1.2, 0xaaaaaa, 0.35);
      g.strokeCircle(cx, cy, ICON_R);
    }
  }
}
